import {
    Button,
    DialogActionButton,
    saveCallbackStatus,
    Toggle,
} from '@finpay-development/shared-components';
import {
    Estimate,
    CrossoverSelectionEnum,
    QuoteMethod,
} from '@finpay-estimation/estimation-types';
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import {useFormik} from 'formik';
import {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {formatNumberToUSD} from '@finpay-development/shared-platform/lib/esm/shared/calculators';
import {AppDispatch} from '../../../shared/state/store';
import {saveSelectedEstimate} from '@finpay-development/shared-platform/lib/esm/shared/state/estimator-thunk';
import {saveSelectedVob} from '@finpay-development/shared-platform/lib/esm/shared/state/vob-thunk';
import {RootState} from '../../../shared/state/root-reducer';
import {useIsDisabledAfterConvert} from '../../hooks/use-is-disabled-after-convert';

interface CrossoverSelectionModalProps {
    estimate: Estimate;
    open: boolean;
    handleCallbackCancel: () => void;
    handleSave: (selection: CrossoverSelectionEnum) => void;
}

const CrossoverSelectionModal = (props: CrossoverSelectionModalProps) => {
    const {open, estimate, handleCallbackCancel, handleSave} = props;

    const vob = useSelector((state: RootState) => {
        return state.vobContext.selectedVob;
    });

    const rateAsPayorPlan = useSelector((state: RootState) => {
        return state.estimatorContext.rateAsPayorPlan;
    });

    const isDisabledAfterConvert = useIsDisabledAfterConvert()

    const [isCarryover, setIsCarryover] = useState(
        vob && typeof vob.isCarryover === 'boolean' ? vob.isCarryover : false
    );
    const [isCopayReset, setIsCopayReset] = useState(
        vob && typeof vob.isCopayReset === 'boolean' ? vob.isCopayReset : false
    );

    const dispatch = useDispatch<AppDispatch>();

    const defaultSelection =
        estimate.crossoverSelection || CrossoverSelectionEnum.BEFORE;

    const [selectedColumn, setSelectedColumn] =
        useState<number>(defaultSelection);

    const hasSelection =
        selectedColumn === CrossoverSelectionEnum.BEFORE ||
        selectedColumn === CrossoverSelectionEnum.WITH;

    // formik dosn't have an easy way to watch for changes
    // and our toggle doesn't allow us to hook into the changes of the input
    // so creating this formik to satisfy toggle's props
    const formik = useFormik({
        initialValues: {
            isCarryover,
            isCopayReset,
        },
        onSubmit: () => {},
    });

    useEffect(() => {
        if (vob) {
            const newIsCarryover = typeof vob.isCarryover === 'boolean' ? vob.isCarryover : false;
            const newIsCopayReset = typeof vob.isCopayReset === 'boolean' ? vob.isCopayReset : false;

            setIsCarryover(newIsCarryover);
            setIsCopayReset(newIsCopayReset);

            formik.setFieldValue('isCarryover', newIsCarryover);
            formik.setFieldValue('isCopayReset', newIsCopayReset);
        }
    }, [vob?.isCarryover, vob?.isCopayReset, vob?.vobId]);

    if (!estimate.crossOverSummary) {
        return <div>Something broke</div>;
    }

    const {summaryBeforeCrossover, summaryWithCrossover} =
        estimate.crossOverSummary;

    const lightBlue = '#E8F6FF';

    const beforeCrossoverBackgroundColor =
        selectedColumn === 0 ? lightBlue : 'white';
    const withCrossoverBackgroundColor =
        selectedColumn === 1 ? lightBlue : 'white';
    const beforeCrossoverButtonType =
        selectedColumn === 0 ? 'primary' : 'secondary';
    const withCrossoverButtonType =
        selectedColumn === 1 ? 'primary' : 'secondary';
    const beforeCrossoverButtonContents =
        selectedColumn === 0 ? 'Selected' : 'Select';
    const withCrossoverButtonContents =
        selectedColumn === 1 ? 'Selected' : 'Select';

    const handleToggleChangeCopay = async () => {
        const newCopayReset = !isCopayReset;
        setIsCopayReset(newCopayReset);

        if (vob && vob.vobId) {
            await dispatch(saveSelectedVob({
                vobId: vob.vobId,
                vob: {
                    ...vob,
                    isCopayReset: newCopayReset,
                    isCarryover: isCarryover // Ensure isCarryover is included
                }
            }));
        }

        if (estimate) {
            const putEstimateBody: Estimate = {
                vobId: estimate.vobId,
                clientId: estimate.clientId,
                facilityId: estimate.facilityId,
                advisorPatientId: estimate.advisorPatientId,
                ...(estimate.patientEncounterId && { patientEncounterId: estimate.patientEncounterId }),
                description: estimate.description,
                quoteMethod: estimate.quoteMethod || QuoteMethod.AVGLOS,
                anticipatedAdmitDate: estimate.anticipatedAdmitDate,
                facilityLevelOfCare: estimate.facilityLevelOfCare,
                isPlanYearCrossover: estimate.isPlanYearCrossover,
                isActive: true,
                crossoverSelection: estimate.crossoverSelection,
                summarySelection: estimate.summarySelection,
                rateAsPayorPlan: rateAsPayorPlan || estimate.rateAsPayorPlan,
            };

            await dispatch(
                saveSelectedEstimate({
                    estimate: putEstimateBody,
                    estimateId: estimate.estimateId,
                })
            );
        }
    };

    const handleToggleChangeCarryover = async () => {
        const newCarryover = !isCarryover;
        setIsCarryover(newCarryover);

        if (vob && vob.vobId) {
            await dispatch(saveSelectedVob({
                vobId: vob.vobId,
                vob: {
                    ...vob,
                    isCarryover: newCarryover,
                    isCopayReset: isCopayReset // Ensure isCopayReset is included
                }
            }));
        }

        if (estimate) {
            const putEstimateBody: Estimate = {
                vobId: estimate.vobId,
                clientId: estimate.clientId,
                facilityId: estimate.facilityId,
                advisorPatientId: estimate.advisorPatientId,
                ...(estimate.patientEncounterId && { patientEncounterId: estimate.patientEncounterId }),
                description: estimate.description,
                quoteMethod: estimate.quoteMethod || QuoteMethod.AVGLOS,
                anticipatedAdmitDate: estimate.anticipatedAdmitDate,
                facilityLevelOfCare: estimate.facilityLevelOfCare,
                isPlanYearCrossover: estimate.isPlanYearCrossover,
                isActive: true,
                crossoverSelection: estimate.crossoverSelection,
                summarySelection: estimate.summarySelection,
                rateAsPayorPlan: rateAsPayorPlan || estimate.rateAsPayorPlan,
            };

            await dispatch(
                saveSelectedEstimate({
                    estimate: putEstimateBody,
                    estimateId: estimate.estimateId,
                })
            );
        }
    };

    return (
        <Dialog
            test-id="crossover-selection-modal"
            className="modal"
            open={open}
            fullWidth={true}
            maxWidth="lg"
            scroll="body"
        >
            <DialogTitle>
                Crossover PFR Selection{' '}
                <Typography variant="h3">
                    Select a PFR amount you want to use in this estimate
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box display={'flex'}>
                    <Box className="mr-6">
                        <Typography variant="h4">
                            4th Quarter Carryover?
                        </Typography>
                        <Toggle
                            name="isCarryover"
                            value={formik.values.isCarryover}
                            formik={formik}
                            test-id="crossover-modal-isCarryover"
                            onChange={handleToggleChangeCarryover}
                            disabled={isDisabledAfterConvert}
                        />
                    </Box>
                    <Box>
                        <Typography variant="h4">Does Copay Reset?</Typography>
                        <Toggle
                            name="isCopayReset"
                            value={formik.values.isCopayReset}
                            formik={formik}
                            test-id="crossover-modal-isCopayReset"
                            onChange={handleToggleChangeCopay}
                            disabled={isDisabledAfterConvert}
                        />
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(5, 1fr)',
                        gridTemplateRows: 'auto',
                        gridTemplateAreas: `"line-items line-items line-items before-crossover with-crossover"
                                "line-items line-items line-items before-crossover with-crossover" 
                                "line-items line-items line-items before-crossover with-crossover"
                                "line-items line-items line-items before-crossover with-crossover"
                                "line-items line-items line-items before-crossover with-crossover"
                                "line-items line-items line-items before-crossover with-crossover"
                                "line-items line-items line-items before-crossover with-crossover"
                                "line-items line-items line-items before-crossover with-crossover"
                                "line-items line-items line-items before-crossover with-crossover"
                                "line-items line-items line-items before-crossover with-crossover"`,
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridArea: 'line-items',
                            gridTemplateColumns: 'subgrid',
                            gridTemplateRows: 'subgrid',
                        }}
                    >
                        <Box sx={{gridColumnStart: 1, gridColumnEnd: 4}}></Box>
                        <Box
                            className="p-2"
                            sx={{gridColumnStart: 1, gridColumnEnd: 4}}
                        >
                            Estimated Adjusted Charges
                        </Box>
                        <Box
                            className="p-2"
                            sx={{gridColumnStart: 1, gridColumnEnd: 4}}
                        >
                            Estimated Length of Stay
                        </Box>
                        <Box
                            className="p-2"
                            sx={{gridColumnStart: 1, gridColumnEnd: 4}}
                        >
                            Estimated Deductible
                        </Box>
                        <Box
                            className="p-2"
                            sx={{gridColumnStart: 1, gridColumnEnd: 4}}
                        >
                            Estimated Co-Insurance
                        </Box>
                        <Box
                            className="p-2"
                            sx={{gridColumnStart: 1, gridColumnEnd: 4}}
                        >
                            Estimated Co-Pay
                        </Box>
                        <Box
                            className="p-2"
                            sx={{gridColumnStart: 1, gridColumnEnd: 4}}
                        >
                            Out of Pocket Max Remaining
                        </Box>
                        <Box
                            className="p-2"
                            sx={{gridColumnStart: 1, gridColumnEnd: 4}}
                        >
                            PFR of Covered Charges
                        </Box>
                        <Box
                            className="p-2"
                            sx={{gridColumnStart: 1, gridColumnEnd: 4}}
                        >
                            PFR of Uncovered Charges
                        </Box>
                        <Box
                            className="p-2"
                            sx={{gridColumnStart: 1, gridColumnEnd: 4}}
                        >
                            Estimated Patient Financial Responsibility
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'grid',
                            gridArea: 'before-crossover',
                            backgroundColor: beforeCrossoverBackgroundColor,
                            gridTemplateColumns: 'subgrid',
                            gridTemplateRows: 'subgrid',
                        }}
                    >
                        <Box
                            className="p-2"
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Typography
                                variant="h2"
                                style={{
                                    textAlign: 'center',
                                }}
                            >
                                Estimate Before Crossover
                            </Typography>
                            <Button
                                type={beforeCrossoverButtonType}
                                onClick={() => {
                                    setSelectedColumn(0);
                                }}
                                test-id="crossover-selection-before-crossover-button"
                            >
                                {beforeCrossoverButtonContents}
                            </Button>
                        </Box>
                        <Box
                            className="py-2 px-5"
                            sx={{gridColumnStart: 4, gridColumnEnd: 5}}
                        >
                            {formatNumberToUSD(
                                summaryBeforeCrossover.coveredCharges
                            )}
                        </Box>
                        <Box
                            className="py-2 px-5"
                            sx={{gridColumnStart: 4, gridColumnEnd: 5}}
                        >
                            {summaryBeforeCrossover.lengthOfStay}
                        </Box>
                        <Box
                            className="py-2 px-5"
                            sx={{gridColumnStart: 4, gridColumnEnd: 5}}
                        >
                            {formatNumberToUSD(
                                summaryBeforeCrossover.deductibleSpent
                            )}
                        </Box>
                        <Box
                            className="py-2 px-5"
                            sx={{gridColumnStart: 4, gridColumnEnd: 5}}
                        >
                            {formatNumberToUSD(
                                summaryBeforeCrossover.patientTotalCoInsurance
                            )}
                        </Box>
                        <Box
                            className="py-2 px-5"
                            sx={{gridColumnStart: 4, gridColumnEnd: 5}}
                            test-id="crossover-modal-before-copay"
                        >
                            {formatNumberToUSD(
                                summaryBeforeCrossover.patientTotalCoPays
                            )}
                        </Box>
                        <Box
                            className="py-2 px-5"
                            sx={{gridColumnStart: 4, gridColumnEnd: 5}}
                        >
                            {formatNumberToUSD(
                                summaryBeforeCrossover.remainingOopMaxFromVob
                            )}
                        </Box>
                        <Box
                            className="py-2 px-5"
                            sx={{gridColumnStart: 4, gridColumnEnd: 5}}
                        >
                            {formatNumberToUSD(
                                summaryBeforeCrossover.coveredSvcsPfr
                            )}
                        </Box>
                        <Box
                            className="py-2 px-5"
                            sx={{gridColumnStart: 4, gridColumnEnd: 5}}
                        >
                            {formatNumberToUSD(
                                summaryBeforeCrossover.unCoveredCharges
                            )}
                        </Box>
                        <Box
                            className="py-2 px-5"
                            sx={{gridColumnStart: 4, gridColumnEnd: 5}}
                        >
                            {formatNumberToUSD(summaryBeforeCrossover.totalPFR)}
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridArea: 'with-crossover',
                            backgroundColor: withCrossoverBackgroundColor,
                            gridTemplateColumns: 'subgrid',
                            gridTemplateRows: 'subgrid',
                        }}
                    >
                        <Box
                            className="p-2"
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Typography
                                variant="h2"
                                style={{
                                    textAlign: 'center',
                                }}
                            >
                                Estimate With Crossover
                            </Typography>

                            <Button
                                test-id="crossover-selection-with-crossover-button"
                                type={withCrossoverButtonType}
                                onClick={() => {
                                    setSelectedColumn(1);
                                }}
                            >
                                {withCrossoverButtonContents}
                            </Button>
                        </Box>
                        <Box
                            className="py-2 px-5"
                            sx={{gridColumnStart: 4, gridColumnEnd: 5}}
                        >
                            {formatNumberToUSD(
                                summaryWithCrossover.coveredCharges
                            )}
                        </Box>
                        <Box className="py-2 px-5">
                            {summaryWithCrossover.lengthOfStay}
                        </Box>
                        <Box className="py-2 px-5">
                            {formatNumberToUSD(
                                summaryWithCrossover.deductibleSpent
                            )}
                        </Box>
                        <Box className="py-2 px-5">
                            {formatNumberToUSD(
                                summaryWithCrossover.patientTotalCoInsurance
                            )}
                        </Box>
                        <Box 
                            className="py-2 px-5"
                            test-id="crossover-modal-with-copay"
                        >
                            {formatNumberToUSD(
                                summaryWithCrossover.patientTotalCoPays
                            )}
                        </Box>
                        <Box className="py-2 px-5">
                            {formatNumberToUSD(
                                summaryWithCrossover.oopMaxFromVob
                            )}
                        </Box>
                        <Box className="py-2 px-5">
                            {formatNumberToUSD(
                                summaryWithCrossover.coveredSvcsPfr
                            )}
                        </Box>
                        <Box className="py-2 px-5">
                            {formatNumberToUSD(
                                summaryWithCrossover.unCoveredCharges
                            )}
                        </Box>
                        <Box className="py-2 px-5">
                            {formatNumberToUSD(summaryWithCrossover.totalPFR)}
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions
                test-id="crossover-modal-dialog-actions"
            >
                <DialogActionButton
                    isEnabled={hasSelection && (!isDisabledAfterConvert)}
                    savebuttonText={'Save'}
                    saveStatus={saveCallbackStatus.success}
                    spinnerLeftPosition={5}
                    executeSave={() => {
                        handleSave(selectedColumn);
                    }}
                    handleCallbackCancel={handleCallbackCancel}
                />
            </DialogActions>
        </Dialog>
    );
};

export default CrossoverSelectionModal; 